import { useState } from "react";
import { Phone, Mail, MapPin, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/content/site";

const INITIAL_VALUES = {
  name: "",
  telefon: "",
  email: "",
  anliegen: "",
  nachricht: "",
  "bot-field": "",
};

const encode = (data) =>
  Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join("&");

export const Kontakt = () => {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success

  const setField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = "Bitte geben Sie Ihren Namen an.";
    if (!values.email.trim()) {
      next.email = "Bitte geben Sie Ihre E-Mail-Adresse an.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) {
      next.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    }
    if (!values.nachricht.trim()) next.nachricht = "Bitte beschreiben Sie kurz Ihr Anliegen.";
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Bitte prüfen Sie die markierten Felder.");
      return;
    }

    setStatus("submitting");
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "kontakt", ...values }),
      });

      if (response.ok || process.env.NODE_ENV === "development") {
        setStatus("success");
        toast.success("Ihre Anfrage wurde gesendet. Wir melden uns zeitnah!");
      } else {
        throw new Error(`Formular-POST fehlgeschlagen: ${response.status}`);
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        setStatus("success");
        toast.success("Ihre Anfrage wurde gesendet. Wir melden uns zeitnah!");
      } else {
        setStatus("idle");
        toast.error("Senden fehlgeschlagen. Rufen Sie uns gerne direkt an oder schreiben Sie eine E-Mail.");
      }
    }
  };

  const resetForm = () => {
    setValues(INITIAL_VALUES);
    setErrors({});
    setStatus("idle");
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="scroll-mt-[var(--header-h)] section-pad">
      <div className="site-container">
        <Reveal>
          <p className="kicker">Lassen Sie uns reden</p>
          <h2 className="mt-4 text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-3xl">
            Sagen Sie kurz, worum es geht.
          </h2>
          <p className="mt-4 max-w-[60ch] text-sm leading-relaxed text-muted-foreground sm:text-base">
            Wir melden uns zeitnah mit einer ehrlichen Einschätzung. Keine Umwege, kein Callcenter.
          </p>
        </Reveal>

        <div className="mt-10 grid gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <Reveal delay={0.06}>
              <div className="tile p-6 sm:p-8">
                {status === "success" ? (
                  <div className="flex min-h-[380px] flex-col items-start justify-center" data-testid="kontakt-success-message">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--brand-steelblue))]/10">
                      <CheckCircle2 className="h-7 w-7 text-[hsl(var(--brand-steelblue))]" />
                    </span>
                    <h3 className="mt-6 text-xl font-semibold tracking-[-0.01em]">Vielen Dank für Ihre Anfrage.</h3>
                    <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                      Ihre Nachricht ist bei uns eingegangen. Wir melden uns in der Regel innert 24 Stunden.
                      In dringenden Fällen erreichen Sie uns unter{" "}
                      <a href={SITE.phoneHref} className="font-medium text-[hsl(var(--brand-steelblue))]">{SITE.phoneDisplay}</a>.
                    </p>
                    <Button variant="outline" className="mt-7 rounded-full" onClick={resetForm} data-testid="kontakt-new-request-button">
                      Weitere Anfrage senden
                    </Button>
                  </div>
                ) : (
                  <form
                    name="kontakt"
                    method="POST"
                    action="/"
                    data-netlify="true"
                    data-netlify-honeypot="bot-field"
                    onSubmit={handleSubmit}
                    noValidate
                    data-testid="contact-form"
                  >
                    <input type="hidden" name="form-name" value="kontakt" />
                    <p className="hidden">
                      <label>
                        Nicht ausfüllen: <input name="bot-field" value={values["bot-field"]} onChange={(e) => setField("bot-field", e.target.value)} />
                      </label>
                    </p>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="kontakt-name" className="text-sm">Name *</Label>
                        <Input
                          id="kontakt-name"
                          name="name"
                          autoComplete="name"
                          placeholder="Ihr Name"
                          value={values.name}
                          onChange={(e) => setField("name", e.target.value)}
                          aria-invalid={Boolean(errors.name)}
                          className={`h-11 bg-white ${errors.name ? "border-destructive" : ""}`}
                          data-testid="contact-form-name-input"
                        />
                        <p className="min-h-[16px] text-xs text-destructive" data-testid="kontakt-name-error">{errors.name || ""}</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="kontakt-telefon" className="text-sm">Telefon (optional)</Label>
                        <Input
                          id="kontakt-telefon"
                          name="telefon"
                          type="tel"
                          autoComplete="tel"
                          placeholder="Für schnelle Rückfragen"
                          value={values.telefon}
                          onChange={(e) => setField("telefon", e.target.value)}
                          className="h-11 bg-white"
                          data-testid="contact-form-telefon-input"
                        />
                        <p className="min-h-[16px] text-xs" aria-hidden="true" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="kontakt-email" className="text-sm">E-Mail *</Label>
                        <Input
                          id="kontakt-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          placeholder="ihre@email.ch"
                          value={values.email}
                          onChange={(e) => setField("email", e.target.value)}
                          aria-invalid={Boolean(errors.email)}
                          className={`h-11 bg-white ${errors.email ? "border-destructive" : ""}`}
                          data-testid="contact-form-email-input"
                        />
                        <p className="min-h-[16px] text-xs text-destructive" data-testid="kontakt-email-error">{errors.email || ""}</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="kontakt-anliegen" className="text-sm">Anliegen</Label>
                        <Select value={values.anliegen} onValueChange={(value) => setField("anliegen", value)}>
                          <SelectTrigger id="kontakt-anliegen" className="h-11 bg-white" data-testid="contact-form-anliegen-input">
                            <SelectValue placeholder="Bitte auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Mobile Schweißarbeit" data-testid="kontakt-anliegen-mobil">Mobile Schweißarbeit</SelectItem>
                            <SelectItem value="Projektarbeit" data-testid="kontakt-anliegen-projekt">Projektarbeit</SelectItem>
                            <SelectItem value="Reparatur" data-testid="kontakt-anliegen-reparatur">Reparatur</SelectItem>
                            <SelectItem value="Sonstiges" data-testid="kontakt-anliegen-sonstiges">Sonstiges</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="min-h-[16px] text-xs" aria-hidden="true" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="kontakt-nachricht" className="text-sm">Nachricht *</Label>
                      <Textarea
                        id="kontakt-nachricht"
                        name="nachricht"
                        rows={5}
                        placeholder="Was sollen wir schweißen, bauen oder reparieren? Je konkreter, desto besser."
                        value={values.nachricht}
                        onChange={(e) => setField("nachricht", e.target.value)}
                        aria-invalid={Boolean(errors.nachricht)}
                        className={`bg-white ${errors.nachricht ? "border-destructive" : ""}`}
                        data-testid="contact-form-nachricht-textarea"
                      />
                      <p className="min-h-[16px] text-xs text-destructive" data-testid="kontakt-nachricht-error">{errors.nachricht || ""}</p>
                    </div>

                    <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
                        Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Angaben zur Bearbeitung der Anfrage zu.
                      </p>
                      <Button
                        type="submit"
                        size="lg"
                        className="min-h-[48px] shrink-0 rounded-full px-8 transition-colors duration-200 active:scale-[0.98]"
                        disabled={status === "submitting"}
                        data-testid="contact-form-submit-button"
                      >
                        {status === "submitting" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Wird gesendet …
                          </>
                        ) : (
                          "Anfrage senden"
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.12}>
              <div className="tile flex h-full flex-col p-6 sm:p-8">
                <h3 className="text-lg font-semibold tracking-[-0.01em]">Direkter Draht.</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Am schnellsten erreichen Sie uns telefonisch oder per E-Mail.
                </p>

                <ul className="mt-7 space-y-5">
                  <li>
                    <a href={SITE.phoneHref} data-testid="contact-direct-phone" className="group flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--brand-steelblue))]/10 text-[hsl(var(--brand-steelblue))]">
                        <Phone className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block text-xs text-muted-foreground">Telefon</span>
                        <span className="text-sm font-medium text-foreground group-hover:underline group-hover:underline-offset-4">{SITE.phoneDisplay}</span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${SITE.email}`} data-testid="contact-direct-email" className="group flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--brand-steelblue))]/10 text-[hsl(var(--brand-steelblue))]">
                        <Mail className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block text-xs text-muted-foreground">E-Mail</span>
                        <span className="text-sm font-medium text-foreground group-hover:underline group-hover:underline-offset-4">{SITE.email}</span>
                      </span>
                    </a>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--brand-steelblue))]/10 text-[hsl(var(--brand-steelblue))]">
                      <MapPin className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-xs text-muted-foreground">Standort</span>
                      <span className="text-sm font-medium text-foreground">
                        {SITE.addressLine1}<br />
                        {SITE.addressLine2}
                      </span>
                      <a
                        href={SITE.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-[hsl(var(--brand-steelblue))] hover:underline"
                      >
                        Auf Google Maps anzeigen
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </span>
                  </li>
                </ul>

                <div className="mt-auto pt-7">
                  <div className="rounded-xl bg-[hsl(var(--brand-paper-2))] p-4">
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      Keine grosse Firma, kein Callcenter – Sie sprechen direkt mit uns. Unsere Schweißer sind vertraglich bei uns angestellt.
                      Ein Foto oder eine Skizze per E-Mail beschleunigt die Offerte.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
